/**
 * API Client Service
 * Centralized HTTP client for making API requests (pattern from ntis-ui reference)
 */

import { appConfig } from '@/config/app.config';
import { getApiBaseUrl } from '@/lib/api/api-base-url';
import { ApiResponse } from '@/types/common.types';

type RequestOptions = RequestInit & {
  timeout?: number;
  retryCount?: number;
  retryDelayMs?: number;
  responseType?: 'json' | 'text' | 'blob';
};

class ApiClient {
  private baseUrl: string;
  private timeout: number;

  constructor() {
    // Browser: default same-origin /api-backend/ (see next.config rewrites) to avoid CORS.
    // Override with NEXT_PUBLIC_API_BASE_URL for direct backend URL.
    const trimmed = getApiBaseUrl();
    this.baseUrl = trimmed.endsWith('/') ? trimmed : `${trimmed}/`;
    this.timeout = appConfig.api.timeout;
  }

  private async request<T>(endpoint: string, options: RequestOptions = {}): Promise<ApiResponse<T>> {
    const {
      timeout: requestTimeout,
      retryCount: rawRetryCount,
      retryDelayMs: rawRetryDelayMs,
      responseType,
      ...fetchOptions
    } = options;
    const timeoutMs = requestTimeout ?? this.timeout;
    const method = String(fetchOptions.method ?? 'GET').toUpperCase();
    // Default retry only for GET to avoid repeating mutating operations.
    const retryCount = rawRetryCount ?? (method === 'GET' ? 2 : 0);
    const retryDelayMs = rawRetryDelayMs ?? 500;
    const headers = new Headers(fetchOptions.headers);
    const isFormData = typeof FormData !== 'undefined' && fetchOptions.body instanceof FormData;
    if (!isFormData && !headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json');
    }

    // Dynamically resolve active council and send to backend
    const rawCouncil = typeof window !== 'undefined' ? localStorage.getItem('councilName') || localStorage.getItem('council') || appConfig.app.municipalCorporation : appConfig.app.municipalCorporation;
    const currentCouncil = (rawCouncil.toLowerCase().includes('panvel') || rawCouncil.includes('पनवेल')) ? 'panvel' : 'thane';
    headers.set('X-Council-Id', currentCouncil);

    // Attach JWT Bearer token if available
    const token = typeof window !== 'undefined' ? sessionStorage.getItem('ptms_auth_token') : null;
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }

    const normalizedEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;

    for (let attempt = 0; attempt <= retryCount; attempt += 1) {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
      try {
        const response = await fetch(`${this.baseUrl}${normalizedEndpoint}`, {
          credentials: 'include',
          ...fetchOptions,
          signal: controller.signal,
          headers,
        });
        clearTimeout(timeoutId);

        let data: T | undefined;
        if (response.status !== 204) {
          if (responseType === 'blob' && response.ok) {
            // Only consume as blob on success — error responses need text for the message
            const blob = await response.blob();
            data = blob as unknown as T;
          } else {
            const contentType = response.headers.get('Content-Type');
            const isJson = contentType?.includes('application/json');
            if (isJson || response.ok) {
              try {
                const text = await response.text();
                if (text) data = JSON.parse(text) as T;
              } catch {
                if (response.ok) {
                  return { success: false, error: 'Invalid JSON response', status: response.status };
                }
                data = undefined;
              }
            } else if (!response.ok) {
              // For non-JSON error responses (e.g. plain text from backend), read as text
              try {
                const text = await response.text();
                if (text) data = { message: text } as unknown as T;
              } catch { /* ignore */ }
            }
          }
        }

        if (!response.ok) {
          // Retry on transient server errors for idempotent methods.
          const retriableStatus = response.status >= 500 || response.status === 408 || response.status === 429;
          if (attempt < retryCount && method === 'GET' && retriableStatus) {
            await new Promise((r) => setTimeout(r, retryDelayMs * (attempt + 1)));
            continue;
          }
          const err = data as { message?: string; title?: string; detail?: string } | undefined;
          const msg =
            err?.message ??
            err?.title ??
            err?.detail ??
            (typeof data === 'string' ? data : undefined) ??
            'An error occurred';
          return {
            success: false,
            error: msg,
            status: response.status,
            data: data as T | undefined,
          };
        }

        return { success: true, data: data as T, status: response.status };
      } catch (error) {
        clearTimeout(timeoutId);
        const isAbort = error instanceof Error && error.name === 'AbortError';
        const message = isAbort
          ? 'Unable to fetch data right now. Please try again.'
          : (error instanceof Error ? error.message : 'Network error');
        if (attempt < retryCount && method === 'GET') {
          await new Promise((r) => setTimeout(r, retryDelayMs * (attempt + 1)));
          continue;
        }
        return {
          success: false,
          error: message,
          status: isAbort ? 408 : 0,
        };
      }
    }

    return {
      success: false,
      error: 'Request failed after retries',
      status: 0,
    };
  }

  async get<T>(endpoint: string, options?: RequestOptions): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { ...options, method: 'GET' });
  }

  async post<T>(endpoint: string, body?: unknown, options?: RequestOptions): Promise<ApiResponse<T>> {
    const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;
    return this.request<T>(endpoint, {
      ...options,
      method: 'POST',
      body: isFormData ? (body as FormData) : (body !== undefined ? JSON.stringify(body) : undefined),
    });
  }

  async put<T>(endpoint: string, body?: unknown, options?: RequestOptions): Promise<ApiResponse<T>> {
    const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;
    return this.request<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: isFormData ? (body as FormData) : (body !== undefined ? JSON.stringify(body) : undefined),
    });
  }

  async delete<T>(endpoint: string, options?: RequestOptions): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { ...options, method: 'DELETE' });
  }
}

export const apiClient = new ApiClient();
